import { Injectable, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap, finalize } from 'rxjs/operators';
import { LoadingService } from '@core/services/loading.service';

@Injectable()
export class LoadingInterceptor implements HttpInterceptor {
  private loadingService = inject(LoadingService);
  private activeRequests = 0;

  /**
   * URLs that should not trigger the global loader
   */
  private excludedUrls = [
    // Background fire-and-forget — should never show a global spinner
    '/leave',
  ];

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip loader for excluded URLs
    if (this.shouldSkipLoader(req.url)) {
      return next.handle(req);
    }

    // Generate unique task ID for this request
    const taskId = this.generateTaskId(req);

    // Show loader when request starts
    this.activeRequests++;
    this.loadingService.show(taskId);

    return next.handle(req).pipe(
      tap({
        next: (event) => {
          // Optional: You can handle specific events here
          if (event instanceof HttpResponse) {
            // Request completed successfully
          }
        },
        error: (error: HttpErrorResponse) => {
          // Request failed - loader will be hidden in finalize
        }
      }),
      finalize(() => {
        // Hide loader when request completes (success or error)
        this.activeRequests--;
        this.loadingService.hide(taskId);

        // Safety check: if no active requests, ensure loader is hidden
        if (this.activeRequests === 0) {
          this.loadingService.hideAll();
        }
      })
    );
  }

  /**
   * Check if loader should be skipped for this URL
   */
  private shouldSkipLoader(url: string): boolean {
    return this.excludedUrls.some(excludedUrl =>
      url.toLowerCase().includes(excludedUrl.toLowerCase())
    );
  }

  /**
   * Generate unique task ID for request tracking
   */
  private generateTaskId(req: HttpRequest<any>): string {
    const timestamp = Date.now();
    const method = req.method;
    const urlPart = req.url.split('?')[0].split('/').slice(-2).join('-');
    return `${method}-${urlPart}-${timestamp}`;
  }
}
